import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs } from '@mui/material';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import Spinner from '@/components/UI/Spinner/Spinner';
import { NONE, statusCampaign, statusSubCampaign } from '@/helpers/constants';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { StatuseSummary, StatuseSummaryGrouped, StatusSummaryAllDto, StatusSummaryGroupedDto } from '@/types';
import Ball from './Ball';
import StatusSummaryCRsTable from './StatusSummaryCRsTable';
import styles from './Dashboard.module.scss';

interface StatusSummaryProps { };

export type StatusConfig = {
  filterDays: number | string;
  groupingLevels: string;
};

const optionsGroupingTrans = {
  ALL: { EN: 'All', DE: 'Alle' },
  PLANT: { EN: 'Plants', DE: 'Werke' },
  MODEL_SERIES: { EN: 'Model Series', DE: 'Modellreihen' },
  DEPARTMENT: { EN: 'Departments', DE: 'Abteilungen' },
};

const optionsToKeyDto = {
  PLANT: 'plants',
  MODEL_SERIES: 'to_affected_series',
  DEPARTMENT: 'departments',
};

const none = NONE.charAt(0) + NONE.slice(1).toLowerCase();

const daysOptions = [none, '14', '30', '60', '90'];

const getCRStatusToSelect = (currentStatusCR: string, statuses: StatuseSummary[]) => {
  const currentStatusFound = statuses.find((status) => status.name === currentStatusCR && (status?.sub_statuses?.length ?? 0) > 0);
  if (currentStatusFound) return currentStatusFound.name;

  return statuses.find((status) => status?.sub_statuses?.length ?? 0 > 0)?.name || '';
};

const StatusSummary: React.FC<StatusSummaryProps> = () => {
  const [isDisplayLoading, setIsDisplayLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const fetchStatusSummary = useApiRequest(campaignServices.getStatusSummaryDashboard);
  const { addToast } = useToastContext();
  const authService = useAuth();
  const [completeStatuses, setCompleteStatuses] = useState<StatuseSummary[]>([]);
  const [statuses, setStatuses] = useState<StatuseSummary[]>([]);
  const [crSelected, setCRSelected] = useState<string>('');
  const [subCRSelected, setSubCRSelected] = useState<string>('');
  const [statusConfig, setStatusConfig] = useState<StatusConfig>({ filterDays: 14, groupingLevels: 'ALL' });
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [currentGroup, setCurrentGroup] = useState<string>('');
  const roleSelected = authService?.roleSelected;

  const getStatusSummaryDashboard = async () => {
    setIsDisplayLoading(true);
    const hasAllOption = statusConfig.groupingLevels === 'ALL';
    if (hasAllOption) setGroupOptions([]);
    const setDays = statusConfig.filterDays === none ? 0 : statusConfig.filterDays;
    await fetchStatusSummary(setDays, hasAllOption ? null : statusConfig.groupingLevels)
      .then((res: StatusSummaryAllDto) => {
        let statuses: StatuseSummary[] = [];

        if (hasAllOption) {
          // order statuses by the order of the statusCampaign
          statuses = Object.values(statusCampaign).reduce((acc: StatuseSummary[], status) => {
            const statusItem = res.aggregations.statuses.find((item) => item.name === status);

            if (statusItem) {
              return acc.concat(statusItem);
            }

            return acc;
          }, []);
        } else {
          const ress = res as StatusSummaryGroupedDto;
          let currentGroupUpdated = res.aggregations[optionsToKeyDto[statusConfig.groupingLevels]][0].name;

          if (currentGroup && res.aggregations[optionsToKeyDto[statusConfig.groupingLevels]].find((item) => item.name === currentGroup)) {
            currentGroupUpdated = currentGroup;
          }
          setCurrentGroup(currentGroupUpdated);
          statuses = Object.values(statusCampaign).reduce((acc: StatuseSummary[], status) => {
            const statusItemOption = ress.aggregations[optionsToKeyDto[statusConfig.groupingLevels]].find((item) => item.name === currentGroupUpdated);
            const statusItem = statusItemOption.statuses.find((item: StatuseSummary) => item.name === status);

            if (statusItem) {
              return acc.concat(statusItem);
            }

            return acc;
          }, []);
          setCompleteStatuses(res.aggregations[optionsToKeyDto[statusConfig.groupingLevels]]);
          const groupOptions = res.aggregations[optionsToKeyDto[statusConfig.groupingLevels]].map((item: StatuseSummaryGrouped) => item.name).sort();
          setGroupOptions(groupOptions);
        }
        setStatuses(statuses);
        setCRSelected(getCRStatusToSelect(crSelected, statuses));
        setSubCRSelected('');
      })
      .catch((error) => {
        console.error(error);
        addToast(error.message, 'ERROR');
      })
      .finally(() => setIsDisplayLoading(false));
  };

  const subCRStatuses = Object.values(statusSubCampaign).reduce((acc: StatuseSummary[], status) => {
    const subcrItems = statuses?.find((status) => status.name === crSelected)?.sub_statuses ?? [];
    const subcrItem = subcrItems?.find((item) => item.name === status);

    if (subcrItem) {
      return acc.concat(subcrItem);
    }

    return acc;
  }, []);

  const onChangeGroupLevel = (group: string) => {
    setCurrentGroup(group);
    const groupStatuse = completeStatuses.find((item) => item.name === group) as StatuseSummaryGrouped;
    const statuses = Object.values(statusCampaign).reduce((acc: StatuseSummary[], status) => {
      const statusItem = groupStatuse?.statuses?.find((item) => item.name === status) ?? [];

      if (statusItem) {
        return acc.concat(statusItem);
      }

      return acc;
    }, []);
    setCRSelected(getCRStatusToSelect(crSelected, statuses));
    setSubCRSelected('');
    setStatuses(statuses);
  };

  const onChangeCRSelected = (status: string) => {
    setCRSelected(status);
    setSubCRSelected('');
  };

  useEffect(() => {
    getStatusSummaryDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStatusSummaryDashboard();
  }, [statusConfig, roleSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={styles.StatusSection}>
        <br />
        <div className={styles.StatusSelection}>
          <div className={styles.ThreeColumns}>
            <Dropdown
              id="filter-past-days"
              variant="filled"
              value={`${statusConfig.filterDays}`}
              options={daysOptions}
              handleChange={(e) => setStatusConfig({ ...statusConfig, filterDays: (e.target.value === none ? e.target.value : parseInt(e.target.value, 10)) })}
              withoutTranslations
              label={t('dashboard.daysInThatStatus')}
              disabled={statuses.length === 0}
            />
          </div>
          <div className={styles.ThreeColumns}>
            <Dropdown
              id="grouping-level"
              variant="filled"
              value={statusConfig.groupingLevels}
              options={optionsGroupingTrans}
              handleChange={(e) => setStatusConfig({ ...statusConfig, groupingLevels: e.target.value })}
              label={t('dashboard.groupingLevels')}
              disabled={statuses.length === 0}
            />
          </div>
          {(groupOptions.length > 0) && (
            <div className={styles.ThreeColumns}>
              <Dropdown
                id={`grouping-level-by-${statusConfig.groupingLevels}`}
                variant="filled"
                value={currentGroup}
                options={groupOptions}
                handleChange={(e) => onChangeGroupLevel(e.target.value)}
                withoutTranslations
                label={t('dashboard.groupingLevelsBy', { group: optionsGroupingTrans[statusConfig.groupingLevels][lang] })}
              />
            </div>
          )}
        </div>
        {statuses.length === 0 && isDisplayLoading
          ? <div className={styles.Spinner}><Spinner isVisible /></div>
          : (
            <>
              <div className={styles.BallsSection}>
                <Tabs
                  variant="scrollable"
                  value={crSelected}
                  scrollButtons="auto"
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  TabScrollButtonProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
                >
                  {statuses?.map((statusItem) => (
                    <Tab
                      key={statusItem.name}
                      value={statusItem.name}
                      disableTouchRipple
                      disableRipple
                      className={styles.FilterTabChip}
                      label={(
                        <Ball selected={statusItem.name === crSelected} statusData={statusItem} onClick={() => onChangeCRSelected(statusItem.name)} isLoading={isDisplayLoading} />
                      )}
                    />
                  ))}
                </Tabs>
              </div>
              <span className={styles.AllCounters}>
                <p>{t('dashboard.subCRByCRStatus', { crStatus: t(`campaign.${crSelected}`) })}</p>
              </span>
              <div className={styles.BallsSection}>
                {(subCRStatuses && subCRStatuses.length > 0)
                  ? subCRStatuses.map((statusItem) => (
                    <Ball key={`key-${statusItem}`} selected={statusItem.name === subCRSelected} statusData={statusItem} onClick={() => setSubCRSelected(statusItem.name === subCRSelected ? '' : statusItem.name)} isLoading={isDisplayLoading} isSubCR />
                  ))
                  : <div className={styles.NoData}>{t('dashboard.noSubCRForCRStatus', { crStatus: t(`campaign.${crSelected}`) })}</div>}
              </div>
              <br />
              <StatusSummaryCRsTable crStatusSelected={crSelected} subCRStatusSelected={subCRSelected} statusConfig={statusConfig} currentGroup={currentGroup} />
            </>
          )}
      </div>
    </>
  );
};
export default StatusSummary;
